import type { UiTheme } from "./theme.types";

const mutableDefaults: UiTheme = {
  colors: {
    primary: "#2563eb",
    primaryHover: "#1d4ed8",
    primarySoft: "color-mix(in srgb, #2563eb 5%, #ffffff 95%)",
    onPrimary: "#ffffff",
    danger: "#dc2626",
    dangerDark: "#b91c1c",
    dangerSoft: "color-mix(in srgb, #dc2626 10%, #ffffff 90%)",
    onDanger: "#ffffff",
    success: "#16a34a",
    successDark: "#15803d",
    successSoft: "color-mix(in srgb, #16a34a 10%, #ffffff 90%)",
    warning: "#d97706",
    warningDark: "#b45309",
    warningSoft: "color-mix(in srgb, #d97706 10%, #ffffff 90%)",
    background: "#ffffff",
    surface: "#f8fafc",
    border: "#e2e8f0",
    textPrimary: "#0f172a",
    textSecondary: "#64748b",
    progressPlaceholder: "#e2e8f0",
    focusRing: "#2563eb",
    overlay: "rgb(0 0 0 / 40%)",
  },
  typography: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', 'Noto Sans', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
    serifFontFamily: "ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
    fontSizes: {
      xs: "0.75rem", sm: "0.875rem", base: "1rem", lg: "1.125rem", xl: "1.25rem",
      "2xl": "1.5rem", "3xl": "1.875rem", "4xl": "2.25rem", "5xl": "3rem",
      "6xl": "3.75rem", "7xl": "4.5rem", "8xl": "6rem", "9xl": "8rem",
    },
    lineHeights: {
      xs: "1rem", sm: "1.25rem", base: "1.5rem", lg: "1.75rem", xl: "1.75rem",
      "2xl": "2rem", "3xl": "2.25rem", "4xl": "2.5rem", "5xl": "1",
      "6xl": "1", "7xl": "1", "8xl": "1", "9xl": "1",
    },
    fontWeights: { normal: 400, medium: 500, semibold: 600, bold: 700 },
    letterSpacing: { tight: "-0.025em", normal: "0em", wide: "0.025em" },
  },
  spacing: { unit: "0.25rem" },
  radii: {
    xs: "0.125rem", sm: "0.25rem", md: "0.375rem", lg: "0.5rem", xl: "0.75rem",
    "2xl": "1rem", "3xl": "1.5rem", "4xl": "2rem", full: "9999px",
  },
  shadows: {
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    "2xl": "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  },
  components: {
    button: {
      radius: "var(--ui-radius-md)",
      sizes: {
        sm: { minHeight: "2.75rem", paddingX: "calc(var(--ui-spacing-unit) * 2.5)", paddingY: "var(--ui-spacing-unit)", fontSize: "var(--ui-font-size-xs)" },
        md: { minHeight: "2.75rem", paddingX: "calc(var(--ui-spacing-unit) * 3)", paddingY: "calc(var(--ui-spacing-unit) * 1.5)", fontSize: "var(--ui-font-size-sm)" },
      },
    },
    field: { radius: "var(--ui-radius-md)", minHeight: "2.75rem", paddingX: "calc(var(--ui-spacing-unit) * 3)", paddingY: "calc(var(--ui-spacing-unit) * 2)", fontSize: "var(--ui-font-size-base)" },
    card: {
      radius: "var(--ui-radius-lg)",
      padding: {
        none: "0",
        compact: "calc(var(--ui-spacing-unit) * 3)",
        default: "calc(var(--ui-spacing-unit) * 4)",
        comfortable: "calc(var(--ui-spacing-unit) * 5)",
      },
      responsivePadding: { compact: "calc(var(--ui-spacing-unit) * 4)", comfortable: "calc(var(--ui-spacing-unit) * 6)" },
    },
    iconButton: { radius: "var(--ui-radius-md)", sizes: { sm: "calc(var(--ui-spacing-unit) * 7)", md: "calc(var(--ui-spacing-unit) * 9)" } },
    menu: {
      minWidth: "10rem", radius: "var(--ui-radius-md)", padding: "var(--ui-spacing-unit)",
      itemPaddingX: "calc(var(--ui-spacing-unit) * 2)", itemPaddingY: "calc(var(--ui-spacing-unit) * 1.5)",
      fontSize: "var(--ui-font-size-sm)", shadow: "var(--ui-shadow-lg)",
    },
    modal: {
      maxWidth: "32rem", radius: "var(--ui-radius-lg)", padding: "calc(var(--ui-spacing-unit) * 4)",
      headerGap: "calc(var(--ui-spacing-unit) * 3)", bodyGap: "calc(var(--ui-spacing-unit) * 3)",
      footerGap: "calc(var(--ui-spacing-unit) * 4)", actionGap: "calc(var(--ui-spacing-unit) * 2)",
      titleFontSize: "var(--ui-font-size-lg)",
    },
    pageShell: {
      maxWidth: "72rem", gap: "calc(var(--ui-spacing-unit) * 6)",
      paddingX: "calc(var(--ui-spacing-unit) * 4)", paddingY: "calc(var(--ui-spacing-unit) * 6)",
      desktopPaddingX: "calc(var(--ui-spacing-unit) * 6)",
    },
    pageHeader: {
      titleFontSize: "var(--ui-font-size-2xl)",
      titleFontFamily: "var(--ui-font-family)",
      titleFontWeight: "var(--ui-font-weight-semibold)",
      titleLineHeight: "inherit",
      descriptionFontSize: "var(--ui-font-size-sm)",
      gap: "calc(var(--ui-spacing-unit) * 4)",
    },
    feedback: {
      spinnerSize: "calc(var(--ui-spacing-unit) * 5)", spinnerBorderWidth: "2px",
      progressHeight: "calc(var(--ui-spacing-unit) * 1.5)", focusRingWidth: "2px",
      focusRingOffset: "2px", disabledOpacity: 0.5,
    },
  },
  accessibility: { minimumControlHeight: "44px" },
  extensions: { colors: {}, spacing: {}, sizes: {}, fontSizes: {}, radii: {}, shadows: {} },
};

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value)) deepFreeze(child);
  }
  return value;
}

export const DEFAULT_UI_THEME: UiTheme = deepFreeze(mutableDefaults);
